import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconCalendarTableCourses,
  iconEditTableCourses,
  iconPersonTableCourses,
  iconStatusTableCourses,
} from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import type { Theme } from '../../theme';
import { useTheme } from '../../theme';
import { CoursesFilter } from './CoursesFilter';
import type {
  CourseListItem,
  CoursesProps,
  FetchCoursesResponse,
} from './types';

const DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 10;
const TABLE_ICON_SIZE = 16;
const ACTION_ICON_SIZE = 16;

/** Objeto multilíngue ex.: { en: string, es: string } */
type MultilingualValue = Record<string, string>;
type TranslationItem = {
  languageCode?: string;
  title?: string;
  description?: string;
  name?: string;
  text?: string;
};

function isMultilingualValue(v: unknown): v is MultilingualValue {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return typeof o.en === 'string' || typeof o.es === 'string';
}

function isTranslationItem(v: unknown): v is TranslationItem {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return false;
  return true;
}

function getTextFromTranslations(translations: unknown, lang?: string): string {
  if (!Array.isArray(translations)) return '';
  const items = translations.filter(isTranslationItem);
  if (items.length === 0) return '';

  const preferred = lang?.toLowerCase().startsWith('es') ? 'es-ES' : 'en-US';
  const fallback = preferred === 'en-US' ? 'es-ES' : 'en-US';
  const pickText = (item?: TranslationItem) =>
    item?.title?.trim() ||
    item?.description?.trim() ||
    item?.name?.trim() ||
    item?.text?.trim() ||
    '';

  const preferredText = pickText(
    items.find(item => item.languageCode === preferred)
  );
  if (preferredText) return preferredText;

  const fallbackText = pickText(
    items.find(item => item.languageCode === fallback)
  );
  if (fallbackText) return fallbackText;

  return items.map(pickText).find(Boolean) ?? '';
}

/**
 * Extrai um texto exibível a partir de título/descrição que pode vir
 * como string ou como objeto multilíngue { en, es } da API.
 * Usa o idioma preferido (lang) ou fallback na ordem en → es → primeiro disponível.
 */
function getDisplayText(value: unknown, lang?: string): string {
  if (value == null) return '';
  const fromTranslations = getTextFromTranslations(value, lang);
  if (fromTranslations) return fromTranslations;
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (!isMultilingualValue(value)) return '';
  const preferred = lang?.toLowerCase().startsWith('es') ? 'es' : 'en';
  const fallback = preferred === 'en' ? 'es' : 'en';
  return (
    value[preferred]?.trim() ||
    value[fallback]?.trim() ||
    Object.values(value)
      .find(v => typeof v === 'string' && v.trim() !== '')
      ?.trim() ||
    ''
  );
}

function formatCourseDate(value: string | undefined): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function getStatusBadgeStyle(
  status: string | undefined,
  theme: Theme
): React.CSSProperties {
  const base = {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.shape.borderRadius.sm2,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.medium,
  };
  const normalizedStatus = String(status ?? '')
    .trim()
    .toLowerCase()
    // Remove accents/diacritics so "borrador" and "borrá dor" match.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Keep only alpha-numeric.
    .replace(/[^a-z0-9]/g, '');

  // Importante: o backend pode retornar `statusName` traduzido.
  // Esse badge precisa funcionar mesmo quando o idioma muda.
  if (
    normalizedStatus === 'draft' ||
    normalizedStatus === 'borrador' ||
    normalizedStatus === 'borrado' ||
    normalizedStatus === 'rascunho'
  )
    return {
      ...base,
      backgroundColor: 'rgba(234, 179, 8, 0.15)',
      border: `${theme.shape.borderWidth.thin} solid rgba(234, 179, 8, 0.4)`,
      color: '#a16207',
    };
  if (
    normalizedStatus === 'published' ||
    normalizedStatus === 'publicado' ||
    normalizedStatus === 'publicada' ||
    normalizedStatus === 'publicados' ||
    normalizedStatus === 'publicadas'
  )
    return {
      ...base,
      backgroundColor: theme.palette.status.background,
      border: `${theme.shape.borderWidth.thin} solid ${theme.palette.status.border}`,
      color: theme.palette.status.text,
    };
  if (
    normalizedStatus === 'archived' ||
    normalizedStatus === 'archivado' ||
    normalizedStatus === 'archivada' ||
    normalizedStatus === 'archivados' ||
    normalizedStatus === 'archivadas' ||
    normalizedStatus === 'arquivado' ||
    normalizedStatus === 'arquivada' ||
    normalizedStatus === 'arquivados' ||
    normalizedStatus === 'arquivadas'
  )
    return {
      ...base,
      backgroundColor: 'rgba(107, 114, 128, 0.15)',
      border: `${theme.shape.borderWidth.thin} solid rgba(107, 114, 128, 0.3)`,
      color: '#4b5563',
    };
  return {
    ...base,
    backgroundColor: theme.palette.status.background,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.status.border}`,
    color: theme.palette.status.text,
  };
}

export function Courses({
  fetchCourses,
  categoryOptions = [],
  statusOptions = [],
  title,
  subtitle,
  searchPlaceholder,
  createButtonLabel,
  onCreateCourse,
  onEdit,
  onEditStatus,
  onDelete: _onDelete,
  searchDebounceMs = DEBOUNCE_MS,
}: CoursesProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const currentLang = i18n.language ?? 'en';

  const titleText = title ?? t('courses.title');
  const subtitleText = subtitle ?? t('courses.subtitle');
  const searchPlaceholderText =
    searchPlaceholder ?? t('courses.search_placeholder');
  const createLabelText = createButtonLabel ?? t('courses.create_button');

  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [dateCreated, setDateCreated] = useState('');
  const debouncedSearch = useDebounce(keyword, searchDebounceMs);

  const [data, setData] = useState<CourseListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res: FetchCoursesResponse = await fetchCourses({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
        categoryId: category || undefined,
        statusId: status || undefined,
        dateCreated: dateCreated || undefined,
      });
      setData(res.data);
      setTotal(res.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    fetchCourses,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    category,
    status,
    dateCreated,
  ]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  const categoryFilterOptions = useMemo(() => {
    const all = { value: '', label: t('courses.all_categories') };
    return [all, ...categoryOptions];
  }, [t, categoryOptions]);

  const statusFilterOptions = useMemo(() => {
    const all = { value: '', label: t('courses.all_status') };
    return [all, ...statusOptions];
  }, [t, statusOptions]);

  const columns = useMemo<ColumnDef<CourseListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('courses.column_title'),
        cell: ({ row }) => {
          const c = row.original;
          const raw = c.translations ?? c.name ?? c.title ?? c.description;
          const titleText = getDisplayText(raw, currentLang) || '—';
          return (
            <div
              style={{
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {titleText}
            </div>
          );
        },
      },
      {
        accessorKey: 'category',
        header: t('courses.column_category'),
        cell: ({ row }) => {
          const cat = row.original.categoryName ?? row.original.category ?? '—';
          return (
            <span style={{ color: theme.palette.text.primary }}>{cat}</span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t('courses.column_status'),
        cell: ({ row }) => {
          const s = row.original.statusName ?? row.original.status ?? '—';
          return <span style={getStatusBadgeStyle(String(s), theme)}>{s}</span>;
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('courses.column_created'),
        cell: ({ row }) => {
          const date = row.original.createdAt ?? row.original.created;
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: theme.palette.text.secondary,
                fontSize: theme.typography.fontSize.sm,
                whiteSpace: 'nowrap',
              }}
            >
              <img
                src={iconCalendarTableCourses}
                alt=""
                width={TABLE_ICON_SIZE}
                height={TABLE_ICON_SIZE}
                aria-hidden
                style={{ flexShrink: 0, marginRight: 4 }}
              />
              {formatCourseDate(date)}
            </span>
          );
        },
      },
      {
        accessorKey: 'modules',
        header: t('courses.column_modules'),
        cell: ({ row }) => {
          const n = row.original.modulesCount ?? row.original.modules ?? 0;
          return (
            <span style={{ color: theme.palette.text.primary }}>
              {Number(n)}
            </span>
          );
        },
      },
      {
        accessorKey: 'enrolled',
        header: t('courses.column_enrolled'),
        cell: ({ row }) => {
          const n = row.original.enrolledCount ?? row.original.enrolled ?? 0;
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: theme.palette.text.primary,
              }}
            >
              <img
                src={iconPersonTableCourses}
                alt=""
                width={TABLE_ICON_SIZE}
                height={TABLE_ICON_SIZE}
                aria-hidden
                style={{ flexShrink: 0, marginRight: 4 }}
              />
              {Number(n)}
            </span>
          );
        },
      },
      {
        accessorKey: 'completion',
        header: t('courses.column_completion'),
        cell: ({ row }) => {
          const pct =
            row.original.completionPercent ?? row.original.completion ?? 0;
          const value = Math.min(100, Math.max(0, Number(pct)));
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                minWidth: 100,
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: theme.shape.borderRadius.full,
                  backgroundColor: theme.palette.border.main,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${value}%`,
                    height: '100%',
                    backgroundColor: theme.palette.status.text,
                    borderRadius: theme.shape.borderRadius.full,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                  minWidth: 32,
                }}
              >
                {value.toFixed(0)}%
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: t('courses.column_actions'),
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.xs,
                width: '100%',
              }}
            >
              {onEdit && (
                <button
                  type="button"
                  className="courses-btn-icon"
                  onClick={() => onEdit(c)}
                  aria-label={t('courses.edit_aria')}
                  data-e2e="courses-edit-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    padding: theme.spacing.xs,
                  }}
                >
                  <img
                    src={iconEditTableCourses}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
              {onEditStatus && (
                <button
                  type="button"
                  className="courses-btn-icon"
                  onClick={() => onEditStatus(c)}
                  aria-label={t('courses.edit_status_aria')}
                  data-e2e="courses-edit-status-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    padding: theme.spacing.xs,
                  }}
                >
                  <img
                    src={iconStatusTableCourses}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [theme, onEdit, onEditStatus, t, currentLang]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount,
    state: { pagination },
    onPaginationChange: updater => {
      setPagination(prev =>
        typeof updater === 'function' ? updater(prev) : prev
      );
    },
  });

  const containerStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    fontFamily: theme.typography.fontFamily,
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xs,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize['2xl'],
    color: theme.palette.text.primary,
  };

  const subtitleStyle: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.secondary,
  };

  const tableWrapperStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  };

  const ACTION_COLUMN_WIDTH = 120;
  const STATUS_COLUMN_WIDTH = 100;
  const CREATED_COLUMN_WIDTH = 150;
  const MODULES_COLUMN_WIDTH = 80;
  const ENROLLED_COLUMN_WIDTH = 90;
  const COMPLETION_COLUMN_WIDTH = 140;

  const getThWidth = (columnId: string): React.CSSProperties => {
    if (columnId === 'actions')
      return {
        width: ACTION_COLUMN_WIDTH,
        minWidth: ACTION_COLUMN_WIDTH,
        maxWidth: ACTION_COLUMN_WIDTH,
      };
    if (columnId === 'status')
      return { width: STATUS_COLUMN_WIDTH, minWidth: STATUS_COLUMN_WIDTH };
    if (columnId === 'createdAt')
      return { width: CREATED_COLUMN_WIDTH, minWidth: CREATED_COLUMN_WIDTH };
    if (columnId === 'modules')
      return { width: MODULES_COLUMN_WIDTH, minWidth: MODULES_COLUMN_WIDTH };
    if (columnId === 'enrolled')
      return { width: ENROLLED_COLUMN_WIDTH, minWidth: ENROLLED_COLUMN_WIDTH };
    if (columnId === 'completion')
      return {
        width: COMPLETION_COLUMN_WIDTH,
        minWidth: COMPLETION_COLUMN_WIDTH,
      };
    return {};
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    verticalAlign: 'middle',
  };

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  };

  const buttonClickStyle = `
    .courses-btn-icon { transition: transform 0.12s ease, opacity 0.12s ease; }
    .courses-btn-icon:active { transform: scale(0.9); opacity: 0.8; }
    .courses-btn { transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease; }
    .courses-btn:active { transform: scale(0.97); }
  `;

  return (
    <div style={containerStyle} data-e2e="courses-list">
      <style>{buttonClickStyle}</style>
      <h1 style={headerStyle}>{titleText}</h1>
      <p style={subtitleStyle}>{subtitleText}</p>

      <CoursesFilter
        searchValue={keyword}
        onSearchChange={setKeyword}
        searchPlaceholder={searchPlaceholderText}
        searchAriaLabel={t('courses.search_aria')}
        categoryOptions={categoryFilterOptions}
        categoryValue={category}
        onCategoryChange={setCategory}
        statusOptions={statusFilterOptions}
        statusValue={status}
        onStatusChange={setStatus}
        dateValue={dateCreated}
        onDateChange={setDateCreated}
        onCreateCourse={onCreateCourse}
        createButtonLabel={createLabelText}
        createAriaLabel={t('courses.create_aria')}
        dataE2eSearchInput="courses-search-input"
        dataE2eCreateButton="courses-create-button"
      />

      <div style={tableWrapperStyle}>
        {loading ? (
          <div
            style={{
              padding: theme.spacing['2xl'],
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            {t('courses.loading')}
          </div>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          ...thStyle,
                          ...getThWidth(header.column.id),
                          ...(header.column.id === 'actions'
                            ? { textAlign: 'center' }
                            : {}),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const courseId =
                    (row.original as Partial<CourseListItem>)?.id ?? undefined;
                  return (
                    <tr
                      key={row.id}
                      data-e2e={
                        courseId ? `courses-row-${courseId}` : 'courses-row'
                      }
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            ...tdStyle,
                            ...getThWidth(cell.column.id),
                            ...(cell.column.id === 'actions'
                              ? { textAlign: 'center' }
                              : {}),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={paginationStyle}>
              <span
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('courses.pagination_summary', {
                  count: data.length,
                  total,
                })}
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                }}
              >
                <button
                  type="button"
                  className="courses-btn"
                  disabled={!table.getCanPreviousPage()}
                  onClick={() => table.previousPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.secondary,
                    cursor: table.getCanPreviousPage()
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: table.getCanPreviousPage() ? 1 : 0.6,
                  }}
                >
                  {t('courses.previous')}
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                  pageNum => {
                    const isActive = pagination.pageIndex + 1 === pageNum;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className="courses-btn"
                        onClick={() => table.setPageIndex(pageNum - 1)}
                        style={{
                          minWidth: 32,
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          fontSize: theme.typography.fontSize.sm,
                          fontFamily: theme.typography.fontFamily,
                          border: isActive
                            ? 'none'
                            : `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          backgroundColor: isActive
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                          color: isActive
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                          cursor: 'pointer',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  type="button"
                  className="courses-btn"
                  disabled={!table.getCanNextPage()}
                  onClick={() => table.nextPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed',
                    opacity: table.getCanNextPage() ? 1 : 0.6,
                  }}
                >
                  {t('courses.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
